import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import {
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
  startAuthentication,
  startRegistration,
  WebAuthnAbortService,
  type AuthenticationResponseJSON,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';

export interface Passkey {
  id: string;
  name: string;
  created: string;
  lastUsedAt?: string;
  backedUp?: boolean;
  transports: string[];
}

@Injectable({ providedIn: 'root' })
export class PasskeyService {
  private readonly http = inject(HttpClient);

  supportsPasskeys = () => browserSupportsWebAuthn();
  supportsAutofill = () => browserSupportsWebAuthnAutofill();
  cancelCeremony = () => WebAuthnAbortService.cancelCeremony();

  list = () => this.http.get<Passkey[]>('api/user/passkey');
  rename = (id: string, name: string) => this.http.put<Passkey>(`api/user/passkey/${id}`, { name });
  delete = (id: string) => this.http.delete<void>(`api/user/passkey/${id}`);
  loginOptions = () => this.http.post<PublicKeyCredentialRequestOptionsJSON>('api/auth/passkey/options', {});

  async register(name: string): Promise<Passkey> {
    const optionsJSON = await firstValueFrom(
      this.http.post<PublicKeyCredentialCreationOptionsJSON>('api/user/passkey/options', {})
    );
    const response = await startRegistration({ optionsJSON });
    return firstValueFrom(this.http.post<Passkey>('api/user/passkey', { name, response }));
  }

  /** Conditional-mediation (autofill) ceremony. Resolves when the user picks a passkey. */
  async startConditionalLogin(): Promise<AuthenticationResponseJSON> {
    const optionsJSON = await firstValueFrom(this.loginOptions());
    return startAuthentication({ optionsJSON, useBrowserAutofill: true });
  }

  /**
   * Modal ceremony — pops the OS passkey picker straight away (no `useBrowserAutofill`).
   * `startAuthentication` mints a fresh abort signal via `WebAuthnAbortService`, which aborts any
   * conditional ceremony still pending, so no explicit `cancelCeremony()` is needed beforehand.
   */
  async startModalLogin(): Promise<AuthenticationResponseJSON> {
    const optionsJSON = await firstValueFrom(this.loginOptions());
    return startAuthentication({ optionsJSON });
  }
}
